import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { useAuth } from 'contexts/AuthContext';
import { toast } from 'sonner';

const QuickMessageButton = ({ item, itemType, onMessageSent }) => {
  const { user, profile } = useAuth();
  const [showQuickMessage, setShowQuickMessage] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSendQuickMessage = async () => {
    if (!message.trim() || sending) return;

    setSending(true);
    try {
      // Include the assignment/class owner in mentions if different from current user
      const mentions = [];
      if (item.user_id && item.user_id !== user.uid) {
        mentions.push(item.user_id);
      }

      await addDoc(collection(db, 'comments'), {
        item_id: item.id,
        item_type: itemType,
        item_title: item.assignment_title || item.class_title || item.class_name || 'Untitled',
        content: message.trim(),
        author_id: user.uid,
        author_name: profile?.full_name || user.email,
        author_role: profile?.is_admin ? 'admin' : profile?.is_writer ? 'writer' : profile?.is_qa ? 'qa' : 'client',
        created_at: serverTimestamp(),
        parent_id: null,
        read_by: [user.uid],
        mentions: mentions, // Include assignment/class owner
        reactions: {},
        edited: false,
        pinned: false
      });
      
      setMessage('');
      setShowQuickMessage(false);
      toast.success('Message sent');
      onMessageSent && onMessageSent();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendQuickMessage();
    }
    if (e.key === 'Escape') {
      setShowQuickMessage(false);
      setMessage('');
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowQuickMessage(!showQuickMessage)}
        className={`p-2 rounded-lg transition-all duration-200 touch-target-44 ${
          showQuickMessage
            ? 'bg-primary text-white'
            : 'bg-white/10 text-white/60 hover:text-white hover:bg-white/20'
        }`}
        title="Quick message"
      >
        <Icon name="MessageCircle" size={16} />
      </button>

      {showQuickMessage && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          className="mobile-quick-message absolute right-0 top-12 z-50 w-80 max-w-[90vw] bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 shadow-xl"
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-white font-medium text-sm">Quick Message</h4>
              <button
                onClick={() => {
                  setShowQuickMessage(false);
                  setMessage('');
                }}
                className="p-1 rounded text-white/60 hover:text-white hover:bg-white/10 transition-colors"
              >
                <Icon name="X" size={14} />
              </button>
            </div>
            
            <div className="text-white/60 text-xs">
              To: {item.assignment_title || item.class_title || item.class_name || 'Untitled'}
            </div>

            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none text-sm"
              rows={3}
              maxLength={500}
              autoFocus
            />

            <div className="flex items-center justify-between">
              <span className="text-white/40 text-xs">
                {message.length}/500 • Enter to send, Esc to cancel
              </span>
              <button
                onClick={handleSendQuickMessage}
                disabled={sending || !message.trim()}
                className="px-3 py-1.5 bg-primary hover:bg-primary/80 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex items-center gap-2"
              >
                {sending ? (
                  <>
                    <Icon name="Loader2" size={14} className="animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={14} />
                    <span>Send</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default QuickMessageButton;